<template>
  <div class="viewer3d">
    <canvas ref="canvas" class="viewer3d__canvas" touch-action="none"></canvas>
    <div v-if="status" class="viewer3d__status">{{ status }}</div>
  </div>
</template>

<script>
import {
  createMoveCodesAsync,
  disposeDrawWorkers,
  getSize,
  moveCount,
} from "../drawer";
import { fetchEtholData } from "../etholData";

const BABYLON_URL = "https://cdn.babylonjs.com/babylon.js";
const BOX_SIZE = 10;
const INV_255 = 1 / 255;
const MOVE_X = [-1, -1, -1, 0, 0, 0, 1, 1, 1];
const MOVE_Y = [-1, 0, 1, -1, 0, 1, -1, 0, 1];
let babylonLoadPromise = null;

function loadBabylon() {
  if (window.BABYLON) {
    return Promise.resolve();
  }

  if (babylonLoadPromise) {
    return babylonLoadPromise;
  }

  babylonLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = BABYLON_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load 3D engine"));
    document.head.appendChild(script);
  });

  return babylonLoadPromise;
}

function nextFrame() {
  return new Promise((resolve) => requestAnimationFrame(resolve));
}

function parseCellCount(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return Math.floor(parsed);
}

function parseTokenId(value) {
  const tokenId = String(value || "");
  if (!/^\d+$/.test(tokenId)) {
    return null;
  }
  return tokenId;
}

export default {
  name: "Viewer3D",

  data() {
    return {
      disposed: false,
      engine: null,
      resizeHandler: null,
      status: "Loading token data...",
    };
  },

  mounted() {
    this.start();
  },

  beforeUnmount() {
    this.disposed = true;
    disposeDrawWorkers();

    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      this.resizeHandler = null;
    }

    if (this.engine) {
      this.engine.stopRenderLoop();
      this.engine.dispose();
      this.engine = null;
    }
  },

  methods: {
    async start() {
      const startedAt = performance.now();
      let tokenData = null;

      try {
        tokenData = await this.loadTokenData();
      } catch (error) {
        void error;
        if (!this.disposed) {
          this.status = "Could not load token data.";
        }
        return;
      }

      if (this.disposed) {
        return;
      }

      if (!tokenData) {
        this.status = "Etholvant not found.";
        return;
      }

      const seed = tokenData.seed;
      const numCells = tokenData.numCells;
      const tokenId = tokenData.tokenId;
      this.status = "Loading 3D engine...";

      try {
        await loadBabylon();
      } catch (error) {
        void error;
        this.status = "Could not load 3D engine.";
        return;
      }

      if (this.disposed) {
        return;
      }

      const engineLoadedAt = performance.now();
      const count = moveCount(numCells);
      this.status = "Calculating cells...";
      const moves = await createMoveCodesAsync(
        seed,
        count,
        (done, total) => {
          if (total > 0) {
            this.status = "Calculating cells " + Math.floor((done / total) * 100) + "%";
          }
        },
        () => this.disposed
      );

      if (!moves || this.disposed) {
        return;
      }

      const movesReadyAt = performance.now();
      this.status = "Building 3D buffers...";
      const size = getSize(numCells);
      const buffers = await this.createInstanceBuffers(moves, numCells, size);
      if (!buffers || this.disposed) {
        return;
      }

      await nextFrame();
      if (this.disposed) {
        return;
      }

      const canvas = this.$refs.canvas;
      const engine = new window.BABYLON.Engine(canvas, true, {
        preserveDrawingBuffer: false,
        stencil: false,
      });
      const buffersReadyAt = performance.now();
      const scene = this.createScene(engine, canvas, buffers);
      const elapsed = Math.round(performance.now() - startedAt);
      const sceneReadyAt = performance.now();

      this.engine = engine;
      this.status = "";
      console.log(
        "Etholvants 3D viewer ready in " +
          elapsed +
          "ms for " +
          tokenId +
          " (" +
          numCells +
          " cells, engine " +
          Math.round(engineLoadedAt - startedAt) +
          "ms, moves " +
          Math.round(movesReadyAt - engineLoadedAt) +
          "ms, buffers " +
          Math.round(buffersReadyAt - movesReadyAt) +
          "ms, scene " +
          Math.round(sceneReadyAt - buffersReadyAt) +
          "ms)"
      );

      engine.runRenderLoop(() => {
        scene.render();
      });

      this.resizeHandler = () => engine.resize();
      window.addEventListener("resize", this.resizeHandler);
    },

    async loadTokenData() {
      const tokenId = parseTokenId(this.$route.params.tokenId);
      if (tokenId == null) {
        return null;
      }

      const etholData = await fetchEtholData();
      const etholvants = etholData && etholData.etholvants;
      const row = etholvants && etholvants[tokenId];
      if (!Array.isArray(row) || !row[0] || row[1] == null) {
        return null;
      }

      return {
        numCells: parseCellCount(row[1]),
        seed: String(row[0]),
        tokenId: tokenId,
      };
    },

    async createInstanceBuffers(moves, numCells, size) {
      const matrices = new Float32Array(16 * numCells);
      const colors = new Float32Array(4 * numCells);
      const stacks = new Uint32Array(size * size);
      const colorStep = numCells > 0 ? 16777215 / numCells : 0;
      const halfWorldSize = (size * BOX_SIZE) / 2;
      const sizeMinusOne = size - 1;
      let colorValue = 0;
      let instance = 0;
      let matrixOffset = 0;
      let colorOffset = 0;
      let x = Math.floor(size / 2);
      let y = x;

      const appendCell = (cellX, cellY) => {
        const stackIndex = cellY * size + cellX;
        const z = stacks[stackIndex] + 1;
        const color = colorValue | 0;
        stacks[stackIndex] = z;

        matrices[matrixOffset] = 1;
        matrices[matrixOffset + 5] = 1;
        matrices[matrixOffset + 10] = 1;
        matrices[matrixOffset + 12] = BOX_SIZE * cellX - halfWorldSize;
        matrices[matrixOffset + 13] = -BOX_SIZE * cellY + halfWorldSize;
        matrices[matrixOffset + 14] = -BOX_SIZE * z;
        matrices[matrixOffset + 15] = 1;
        matrixOffset += 16;

        colors[colorOffset] = ((color & 16711680) >>> 16) * INV_255;
        colors[colorOffset + 1] = ((color & 65280) >>> 8) * INV_255;
        colors[colorOffset + 2] = (color & 255) * INV_255;
        colors[colorOffset + 3] = 1;
        colorOffset += 4;

        instance += 1;
        colorValue += colorStep;
      };

      if (instance < numCells) {
        appendCell(x, y);
      }

      if (instance < numCells) {
        appendCell(sizeMinusOne - x, y);
      }

      for (let i = 0; i < moves.length && instance < numCells; i += 1) {
        const code = moves[i];
        x += MOVE_X[code];
        y += MOVE_Y[code];

        if (x < 0) {
          x = sizeMinusOne;
        } else if (x > sizeMinusOne) {
          x = 0;
        }

        if (y < 0) {
          y = sizeMinusOne;
        } else if (y > sizeMinusOne) {
          y = 0;
        }

        appendCell(x, y);

        if (instance < numCells) {
          appendCell(sizeMinusOne - x, y);
        }

        if ((instance & 65535) === 0) {
          this.status =
            "Building 3D buffers " + Math.floor((instance / numCells) * 100) + "%";
          await nextFrame();
          if (this.disposed) {
            return null;
          }
        }
      }

      return {
        colors: colors,
        matrices: matrices,
      };
    },

    createScene(engine, canvas, buffers) {
      const BABYLON = window.BABYLON;
      const scene = new BABYLON.Scene(engine);
      scene.clearColor = new BABYLON.Color3(1, 1, 1);
      scene.skipPointerMovePicking = true;

      const camera = new BABYLON.ArcRotateCamera(
        "Camera",
        Math.PI,
        Math.PI / 3,
        2300,
        BABYLON.Vector3.Zero(),
        scene
      );
      camera.wheelPrecision = 0.3;
      camera.upperBetaLimit = null;
      camera.setTarget(new BABYLON.Vector3(0, 0, -100));
      camera.attachControl(canvas, true);
      scene.camera = camera;

      new BABYLON.PointLight("light", new BABYLON.Vector3(10, 10, 0), scene);

      const root = BABYLON.BoxBuilder.CreateBox("root", { size: BOX_SIZE }, scene);
      const material = new BABYLON.StandardMaterial("material", scene);
      root.thinInstanceSetBuffer("matrix", buffers.matrices, 16);
      root.thinInstanceSetBuffer("color", buffers.colors, 4);
      root.material = material;
      root.alwaysSelectAsActiveMesh = true;
      root.enableEdgesRendering();
      root.edgesWidth = 60;
      root.edgesColor = new BABYLON.Color4(0.8, 0.8, 0.8, 0.7);

      material.freeze();
      scene.freezeActiveMeshes();
      return scene;
    },
  },
};
</script>

<style scoped>
.viewer3d {
  background: #ffffff;
  inset: 0;
  overflow: hidden;
  position: fixed;
  z-index: 1000;
}

.viewer3d__canvas {
  display: block;
  height: 100%;
  touch-action: none;
  width: 100%;
}

.viewer3d__status {
  background: rgba(255, 255, 255, 0.82);
  border-radius: 4px;
  bottom: 14px;
  color: #333333;
  font-size: 13px;
  left: 16px;
  line-height: 1.4;
  padding: 6px 8px;
  pointer-events: none;
  position: fixed;
  z-index: 1001;
}
</style>
